import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { RefreshCw, Send, Home, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PracticeFailedPage = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState('loading');
  const [failedQuestions, setFailedQuestions] = useState([]);
  const [totalFailed, setTotalFailed] = useState(0);
  const [examSessionId, setExamSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [numQuestions, setNumQuestions] = useState(10);

  useEffect(() => {
    loadFailedQuestions();
  }, []);

  const loadFailedQuestions = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/failed-questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFailedQuestions(response.data.questions);
      setTotalFailed(response.data.total_failed);
      setStage('setup');
    } catch (error) {
      console.error('Error loading failed questions:', error);
      toast.error('Error al cargar preguntas falladas');
      setStage('setup');
    }
  };

  const startPractice = async () => {
    if (totalFailed === 0) {
      toast.info('¡Felicidades! No tienes preguntas falladas para repasar.');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/practice-failed?num_questions=${numQuestions}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExamSessionId(response.data.exam_session_id);
      setQuestions(response.data.questions);
      setStage('exam');
      toast.success('¡Repaso iniciado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar repaso');
    } finally {
      setLoading(false);
    }
  };

  const submitPractice = async () => {
    const answeredCount = Object.keys(answers).length;
    if (answeredCount < questions.length) {
      if (!window.confirm(`Solo has respondido ${answeredCount} de ${questions.length} preguntas. ¿Deseas enviar?`)) {
        return;
      }
    }

    setLoading(true);
    toast.info('Analizando tus respuestas...');
    
    try {
      const token = localStorage.getItem('session_token');
      
      const answerArray = questions.map((question, index) => ({
        question_id: question.question_id,
        selected_option_id: answers[question.question_id] || null,
        question_index: index + 1
      }));
      
      const response = await axios.post(
        `${API}/exam/submit`,
        { exam_session_id: examSessionId, answers: answerArray },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('¡Repaso completado!');
      navigate(`/results/${examSessionId}`, { state: { results: response.data } });
    } catch (error) {
      console.error('Error submitting practice:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar respuestas');
    } finally {
      setLoading(false);
    }
  };

  if (stage === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Cargando preguntas falladas...</p>
        </div>
      </div>
    );
  }

  if (stage === 'setup') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center">
              <RefreshCw className="w-7 h-7 text-amber-600" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold font-heading">Repasar Preguntas Falladas</h1>
              <p className="text-slate-600">Mejora tus puntos débiles</p>
            </div>
          </div>

          {totalFailed === 0 ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center mb-6">
              <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-emerald-900 mb-2">¡Excelente trabajo!</h2>
              <p className="text-emerald-700">
                No tienes preguntas falladas para repasar. Sigue practicando con tests nuevos.
              </p>
            </div>
          ) : (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-amber-900 mb-1">
                      Tienes {totalFailed} preguntas falladas
                    </h3>
                    <p className="text-amber-700 text-sm">
                      Practica con las preguntas que más te han costado para mejorar tu rendimiento.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <Label className="text-sm font-medium text-slate-700 mb-3 block">
                  Número de preguntas a repasar
                </Label>
                <div className="grid grid-cols-4 gap-3">
                  {[5, 10, 20, totalFailed].filter((n, i, arr) => n <= totalFailed && (i < 3 || arr[2] < n)).map((num) => (
                    <button
                      key={num}
                      onClick={() => setNumQuestions(num)}
                      className={`py-3 px-4 rounded-xl border-2 font-medium transition-all ${
                        numQuestions === num
                          ? 'border-amber-600 bg-amber-50 text-amber-700'
                          : 'border-slate-200 hover:border-amber-300'
                      }`}
                    >
                      {num === totalFailed ? `Todas (${num})` : num}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3 max-h-64 overflow-y-auto border border-slate-200 rounded-xl p-4 mb-6">
                <p className="text-sm font-medium text-slate-600 mb-2">
                  Preguntas más falladas (ordenadas por frecuencia):
                </p>
                {failedQuestions.slice(0, 5).map((q, index) => (
                  <div key={q.question_id} className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg">
                    <XCircle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700 truncate">
                        {q.question || q.text || q.question_text || 'Pregunta sin título'}
                      </p>
                      <p className="text-xs text-rose-600 font-medium">
                        Fallada {q.fail_count} {q.fail_count === 1 ? 'vez' : 'veces'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="flex gap-4">
            <Button
              variant="outline"
              className="flex-1 rounded-full py-6"
              onClick={() => navigate('/dashboard')}
            >
              <Home className="w-5 h-5 mr-2" />
              Volver
            </Button>
            {totalFailed > 0 && (
              <Button
                className="flex-1 rounded-full bg-amber-600 hover:bg-amber-700 text-white py-6 font-bold"
                onClick={startPractice}
                disabled={loading}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                ) : (
                  <RefreshCw className="w-5 h-5 mr-2" />
                )}
                Comenzar Repaso
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Exam stage
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 mb-6 sticky top-4 z-10">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => {
                if (window.confirm('¿Seguro que quieres salir? Perderás el progreso.')) {
                  navigate('/dashboard');
                }
              }}
            >
              <Home className="w-4 h-4 mr-2" />
              Salir
            </Button>
            <div className="text-center">
              <h1 className="text-xl font-bold font-heading">Repaso de Preguntas Falladas</h1>
              <p className="text-slate-600 text-sm">
                Respondidas: {Object.keys(answers).length} / {questions.length}
              </p>
            </div>
            <Button
              className="rounded-full bg-amber-600 hover:bg-amber-700 text-white px-6 font-bold"
              onClick={submitPractice}
              disabled={loading}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              ) : (
                <Send className="w-5 h-5 mr-2" />
              )}
              Enviar
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          {questions.map((question, index) => (
            <div
              key={question.question_id}
              className="bg-white rounded-2xl shadow-md border border-slate-200 p-6"
            >
              <div className="flex items-start gap-3 mb-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm">
                  {index + 1}
                </span>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">
                    {question.question || question.text || question.question_text || 'Pregunta'}
                  </p>
                  {question.fail_count && (
                    <p className="text-xs text-rose-600 mt-1">
                      Has fallado esta pregunta {question.fail_count} {question.fail_count === 1 ? 'vez' : 'veces'}
                    </p>
                  )}
                </div>
              </div>

              <RadioGroup
                value={answers[question.question_id] || ''}
                onValueChange={(value) => setAnswers({ ...answers, [question.question_id]: value })}
              >
                <div className="space-y-3">
                  {question.options?.map((option) => (
                    <label
                      key={option.option_id}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        answers[question.question_id] === option.option_id
                          ? 'border-amber-600 bg-amber-50'
                          : 'border-slate-200 hover:border-amber-300 hover:bg-amber-50/50'
                      }`}
                    >
                      <RadioGroupItem value={option.option_id} id={option.option_id} className="text-amber-600" />
                      <Label htmlFor={option.option_id} className="flex-1 cursor-pointer text-slate-700">
                        {option.text}
                      </Label>
                    </label>
                  ))}
                </div>
              </RadioGroup>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PracticeFailedPage;
